import { create } from 'zustand';
import releaseNotes from '../../assets/release-notes.json';

const RELEASE_NOTES_VERSION_KEY = 'neo-release-notes-version';
const AUTO_OPEN_DELAY = 3000;

interface ReleaseNote {
  version: string;
  date: string;
  changes: string[];
}

interface ReleaseNotesState {
  isOpen: boolean;
  showIntro: boolean;
  isTransitioning: boolean;
  showEnterAnimation: boolean;
  selectedVersion: string;
  latestVersion: string;
  releaseNotes: ReleaseNote[];

  // Actions
  open: () => void;
  close: () => void;
  showReleaseNotesContent: () => void;
  selectVersion: (version: string) => void;
  checkForNewVersion: () => void;
}

const getLatestVersion = (): string => {
  return (releaseNotes as ReleaseNote[])[0]?.version ?? 'unknown';
};

const getDefaultSelectedVersion = (): string => {
  return (releaseNotes as ReleaseNote[])[0]?.version ?? '';
};

export const useReleaseNotesStore = create<ReleaseNotesState>((set, get) => ({
  isOpen: false,
  showIntro: false,
  isTransitioning: false,
  showEnterAnimation: false,
  selectedVersion: getDefaultSelectedVersion(),
  latestVersion: getLatestVersion(),
  releaseNotes: releaseNotes as ReleaseNote[],

  open: () => {
    set({
      isOpen: true,
      showIntro: false,
      isTransitioning: false,
    });
  },

  close: () => {
    const { latestVersion } = get();
    localStorage.setItem(RELEASE_NOTES_VERSION_KEY, latestVersion);
    set({
      isOpen: false,
      showIntro: false,
      isTransitioning: false,
    });
  },

  showReleaseNotesContent: () => {
    const { latestVersion } = get();
    localStorage.setItem(RELEASE_NOTES_VERSION_KEY, latestVersion);
    set({ isTransitioning: true });

    setTimeout(() => {
      set({
        showIntro: false,
        showEnterAnimation: true,
        isTransitioning: false,
      });
      setTimeout(() => set({ showEnterAnimation: false }), 500);
    }, 400);
  },

  selectVersion: (version: string) => {
    set({ selectedVersion: version });
  },

  checkForNewVersion: () => {
    const { latestVersion } = get();
    const storedVersion = localStorage.getItem(RELEASE_NOTES_VERSION_KEY);

    if (!storedVersion || storedVersion !== latestVersion) {
      setTimeout(() => {
        set({
          showIntro: true,
          isOpen: true,
        });
      }, AUTO_OPEN_DELAY);
    }
  },
}));
